import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, Pencil, Trash2, CreditCard, Search, AlertTriangle, Receipt } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { useFABContext } from '@/components/common/FABContext';
import { fetchBills, fetchBillSummary, createBill, updateBill, deleteBill, markBillAsPaid } from '@/features/bills/billsSlice';
import type { Bill } from '@/lib/types';
import { BillForm, type BillFormValues } from '@/components/bills/BillForm';
import BillDetailSheet from '@/components/bills/BillDetailSheet';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import PageGuide from '@/components/common/PageGuide';
import { billsGuide } from '@/components/common/guideContent';

const statusBadgeClass: Record<string, string> = {
  upcoming: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

type FilterTab = 'all' | 'upcoming' | 'paid' | 'overdue';

export default function Bills() {
  useDocumentTitle('Bills');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { fmt } = useCurrency();
  const { bills, summary, isLoading } = useAppSelector((state) => state.bills);

  const [formOpen, setFormOpen] = useState(false);
  const [editingBill, setEditingBill] = useState<Bill | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [billToDelete, setBillToDelete] = useState<Bill | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBill, setDetailBill] = useState<Bill | null>(null);

  const { registerFABActions } = useFABContext();
  useEffect(() => {
    registerFABActions({ onAddBill: () => { setEditingBill(null); setFormOpen(true); } });
    return () => registerFABActions({});
  }, [registerFABActions]);

  const location = useLocation();
  useEffect(() => {
    if ((location.state as { openForm?: boolean } | null)?.openForm) {
      setEditingBill(null);
      setFormOpen(true);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(fetchBills(undefined));
    dispatch(fetchBillSummary());
  }, [dispatch]);

  const openDetail = (bill: Bill) => {
    setDetailBill(bill);
    setDetailOpen(true);
  };

  const filteredBills = useMemo(() => {
    let result = bills;
    if (activeTab !== 'all') {
      result = result.filter((b) => b.status === activeTab);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((b) => b.name.toLowerCase().includes(q));
    }
    return result;
  }, [bills, activeTab, searchQuery]);

  const handleFormSubmit = async (values: BillFormValues) => {
    setIsSubmitting(true);
    try {
      const payload: Partial<Bill> = {
        name: values.name,
        amount: values.amount,
        dueDate: values.dueDate,
        category: values.category || undefined,
        account: values.account || undefined,
        isRecurring: values.isRecurring,
        emoji: values.emoji || undefined,
        notes: values.notes || undefined,
        reminderDays: values.reminderDays,
      };
      if (values.isRecurring && values.recurringFrequency) {
        payload.recurringPattern = {
          frequency: values.recurringFrequency,
          interval: (values.recurringInterval as number) || 1,
        };
      }

      if (editingBill) {
        await dispatch(updateBill({ id: editingBill._id, data: payload })).unwrap();
        toast({ title: 'Bill updated', description: `${values.name} has been saved.` });
      } else {
        await dispatch(createBill(payload)).unwrap();
        toast({ title: 'Bill created', description: `${values.name} is ready to track.` });
      }
      setFormOpen(false);
      setEditingBill(null);
      dispatch(fetchBillSummary());
    } catch (error: unknown) {
      toast({ title: 'Unable to save bill', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBill = async () => {
    if (!billToDelete) return;
    try {
      await dispatch(deleteBill(billToDelete._id)).unwrap();
      toast({ title: 'Bill deleted', description: `${billToDelete.name} removed.` });
      dispatch(fetchBillSummary());
    } catch (error: unknown) {
      toast({ title: 'Unable to delete bill', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    } finally {
      setBillToDelete(null);
    }
  };

  const handlePay = async (bill: Bill) => {
    try {
      await dispatch(markBillAsPaid({ id: bill._id })).unwrap();
      toast({ title: 'Bill paid', description: `${bill.name} marked as paid.` });
      dispatch(fetchBillSummary());
    } catch (error: unknown) {
      toast({ title: 'Unable to mark as paid', description: (error instanceof Error ? error.message : '') || 'Please try again', variant: 'destructive' });
    }
  };

  const renderDropdown = (bill: Bill) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Manage ${bill.name}`} onClick={(e) => e.stopPropagation()}>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(bill.status === 'upcoming' || bill.status === 'overdue') && (
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handlePay(bill); }}>
            <CreditCard className="mr-2 h-4 w-4" /> Pay
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingBill(bill); setFormOpen(true); }}>
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setBillToDelete(bill); }}>
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderContent = () => {
    if (isLoading && bills.length === 0) {
      return (
        <div className="space-y-2">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="flex items-center gap-4 rounded-md border p-4">
              <Skeleton className="h-9 w-9 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      );
    }

    if (bills.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <Receipt className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No bills yet. Add your first bill to start tracking.</p>
          <Button className="mt-4" onClick={() => { setEditingBill(null); setFormOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" /> Add Bill
          </Button>
        </div>
      );
    }

    if (filteredBills.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-sm text-muted-foreground">No bills match your filter.</p>
        </div>
      );
    }

    return (
      <>
        {/* Desktop table */}
        <div className="hidden sm:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden lg:table-cell">Category</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBills.map((bill) => (
                <TableRow key={bill._id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => openDetail(bill)}>
                  <TableCell className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border bg-muted/30 shrink-0">
                      <span className="text-lg leading-none">{bill.emoji || bill.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="font-medium">{bill.name}</div>
                      {bill.isRecurring && bill.recurringPattern && (
                        <div className="text-xs text-muted-foreground capitalize">{bill.recurringPattern.frequency}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-semibold">{fmt(bill.amount)}</TableCell>
                  <TableCell>
                    {new Date(bill.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    {bill.daysUntilDue !== undefined && bill.status !== 'paid' && (
                      <span className={`ml-1 text-xs ${bill.daysUntilDue < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                        ({bill.daysUntilDue < 0 ? `${Math.abs(bill.daysUntilDue)}d ago` : `${bill.daysUntilDue}d`})
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusBadgeClass[bill.status] || ''} variant="outline">{bill.status}</Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {typeof bill.category === 'string' ? bill.category : (bill.category as any)?.name || '—'}
                  </TableCell>
                  <TableCell className="text-right">{renderDropdown(bill)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden space-y-2">
          {filteredBills.map((bill) => (
            <div
              key={bill._id}
              className="flex items-center gap-3 p-3 rounded-lg border bg-card cursor-pointer hover:bg-muted/50 transition-colors min-h-[44px]"
              onClick={() => openDetail(bill)}
            >
              <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-lg border bg-muted/30">
                <span className="text-lg leading-none">{bill.emoji || bill.name.charAt(0).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{bill.name}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{new Date(bill.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                  <span>·</span>
                  <Badge className={`${statusBadgeClass[bill.status] || ''} text-[10px] px-1 py-0`} variant="outline">{bill.status}</Badge>
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="font-semibold text-sm">{fmt(bill.amount)}</p>
              </div>
              <div className="flex-shrink-0">{renderDropdown(bill)}</div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <PageGuide pageKey="bills" content={billsGuide} />
      <div className="flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Bills</h2>
          <p className="text-sm md:text-base text-muted-foreground">Track and pay your bills on time.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => { setEditingBill(null); setFormOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" /> Add Bill
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Upcoming</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{fmt(summary?.totalUpcoming ?? 0)}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{summary?.upcomingCount ?? 0} bills</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Overdue</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl text-red-600 dark:text-red-400">{fmt(summary?.totalOverdue ?? 0)}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{summary?.overdueCount ?? 0} bills</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Paid This Month</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{fmt(summary?.paidThisMonth ?? 0)}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{summary?.paidCount ?? 0} paid</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Monthly Recurring</CardDescription>
            <CardTitle className="truncate text-lg sm:text-2xl">{fmt(summary?.recurringMonthlyTotal ?? 0)}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Recurring total</CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Bills</CardTitle>
              <CardDescription>View, pay, and manage your bills.</CardDescription>
            </div>
            <Button size="sm" onClick={() => { setEditingBill(null); setFormOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" /> Add
            </Button>
          </div>
          <div className="flex flex-col gap-3 mt-2 sm:flex-row sm:items-center">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as FilterTab)} className="w-full sm:w-auto">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                <TabsTrigger value="paid">Paid</TabsTrigger>
                <TabsTrigger value="overdue">Overdue</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search bills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>{renderContent()}</CardContent>
      </Card>

      <BillForm
        open={formOpen}
        onOpenChange={(open) => {
          if (!open) setEditingBill(null);
          setFormOpen(open);
        }}
        onSubmit={handleFormSubmit}
        bill={editingBill}
        isSubmitting={isSubmitting}
      />

      <BillDetailSheet
        bill={detailBill}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={(bill) => { setEditingBill(bill); setFormOpen(true); }}
        onDelete={(bill) => setBillToDelete(bill)}
        onPay={handlePay}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!billToDelete} onOpenChange={(open) => !open && setBillToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete bill
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{billToDelete?.name ?? 'this bill'}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBill} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete Bill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
